import React from 'react';
import { Building2, X } from 'lucide-react';

const BASIS = { day: ['Daily', 'Days'], week: ['Weekly', 'Weeks'], month: ['4-week', 'Periods'], trip: ['Each way', 'Trips'] };
export const rentalBasisLabel = (b) => BASIS[b] || BASIS.day;
const money = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Sunbelt rate-sheet pick for a quote "Equipment Rental" line: cost from the sheet, markup = rental margin.
export default function RentalLinePicker({ line, index, rentals, onPatch }) {
  if (!rentals) return null;
  const { rates = [], margin_pct = 30, vendor = 'Sunbelt Rentals', effective_to } = rentals;
  const picked = line.rental_rate_id ? rates.find(r => r.id === line.rental_rate_id) : null;
  const groups = rates.reduce((g, r) => { (g[r.category] = g[r.category] || []).push(r); return g; }, {});

  const pick = (id) => {
    const r = rates.find(x => x.id === id);
    if (!r) return onPatch({ rental_rate_id: null, rental_basis: null, vendor: '' });
    const basis = r.flat ? 'trip' : 'day';
    onPatch({ rental_rate_id: r.id, rental_basis: basis, vendor, equipment_name: r.name, daily_rate: r.cost[basis], individual_markup_percentage: margin_pct });
  };
  const setBasis = (b) => picked && onPatch({ rental_basis: b, daily_rate: picked.cost[b] });

  const margin = line.individual_markup_percentage ?? margin_pct;
  const basis = line.rental_basis || 'day';
  const sell = Number(line.daily_rate || 0) * (1 + Number(margin) / 100);

  return (
    <div className="col-span-12 flex flex-wrap items-center gap-2 -mb-1" data-testid={`rental-pick-${index}`}>
      <Building2 className="w-3.5 h-3.5 text-orange-600" />
      <select value={line.rental_rate_id || ''} onChange={(e) => pick(e.target.value)} data-testid={`rental-select-${index}`}
        className="h-7 text-xs border border-orange-300 bg-orange-50 text-orange-900 rounded px-1.5 max-w-[26rem]">
        <option value="">Pick from the {vendor} rate sheet…</option>
        {Object.entries(groups).map(([cat, items]) => (
          <optgroup key={cat} label={cat}>
            {items.map(r => <option key={r.id} value={r.id}>{r.name} — {r.flat ? `${money(r.cost.trip)} each way` : `${money(r.cost.day)}/day · ${money(r.cost.week)}/wk · ${money(r.cost.month)}/4wk`} (cost)</option>)}
          </optgroup>
        ))}
      </select>
      {picked && (
        <>
          {!picked.flat && (
            <div className="flex rounded border border-orange-300 overflow-hidden text-[11px]" data-testid={`rental-basis-${index}`}>
              {['day', 'week', 'month'].map(b => (
                <button key={b} type="button" onClick={() => setBasis(b)} data-testid={`rental-basis-${b}-${index}`}
                  className={`px-2 py-0.5 ${basis === b ? 'bg-orange-600 text-white' : 'bg-white text-orange-800 hover:bg-orange-50'}`}>{BASIS[b][0]}</button>
              ))}
            </div>
          )}
          <span className="text-[11px] text-slate-600" data-testid={`rental-hint-${index}`}>
            {vendor} cost {money(line.daily_rate)}/{basis === 'month' ? '4wk' : basis} · {margin}% margin → quoted <b className="text-slate-900">{money(sell)}/{basis === 'month' ? '4wk' : basis}</b>
            {effective_to && <span className="text-slate-400"> · sheet good to {effective_to}</span>}
          </span>
          <button type="button" onClick={() => pick('')} title="Back to a free-text equipment line" data-testid={`rental-clear-${index}`}
            className="text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
        </>
      )}
    </div>
  );
}
